import { cn } from '@/shared/utils/cn';
import { VisualSlotType, IconFontColor, IconType } from '@/shared/types/types';
import React, { useState, useCallback, useMemo, useRef } from 'react';

// Import all molecular components
import { ListItemDefault } from '../molecules/ListItemDefault';
import { ListItemRadio } from '../molecules/ListItemRadio';
import { ListItemCheckbox } from '../molecules/ListItemCheckbox';
import { ListItemToggle } from '../molecules/ListItemToggle';
import { ListItemUser } from '../molecules/ListItemUser';
import { ListSeparator } from '../molecules/ListSeparator';
import { ListEmpty } from '../molecules/ListEmpty';
import { ListHeader } from '../molecules/ListHeader';
import { ListSearchInput } from '../molecules/ListSearchInput';

// Import drag and drop utilities
import { useDragAndDrop } from '@/shared/hooks';
import { reorder } from '@/shared/utils';
import { Icon } from '@/modules/Icon/Icon';
import { Checkbox } from '@/modules/Checkbox';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { warnDeprecatedListBaseFlatPropsIfNeeded } from '../../utils/warnDeprecatedListBaseFlatProps';

// ============================================================================
// TYPES FOR EACH LIST VARIANT
// ============================================================================

// Base item types
export interface BaseListItem {
  id: string;
  disabled?: boolean;
}

export interface DefaultListItem extends BaseListItem {
  label: string;
  icon?: VisualSlotType;
  iconColor?: IconFontColor;
  color?: 'blue' | 'red';
}

export interface RadioListItem extends BaseListItem {
  label: string;
  count?: string | number;
}

export interface CheckboxListItem extends BaseListItem {
  title: string;
  description?: string;
  checked?: boolean;
}

export interface ToggleListItem extends BaseListItem {
  title: string;
  description?: string;
  checked?: boolean;
  draggable?: boolean;
}

export interface UserListItem extends BaseListItem {
  name: string;
  subtitle?: string;
  avatar?: React.ReactNode | string;
  badge?: string;
  badgeVariant?: 'success' | 'warning' | 'error' | 'info';
  selected?: boolean;
}

export interface FilterListItem extends BaseListItem {
  label: string;
  checked?: boolean;
}

/** Ítem de lista para la variante con búsqueda y multiselección (cabecera + filas con checkbox). */
export interface SearchMultiCheckboxListItem extends BaseListItem {
  label: string;
  checked?: boolean;
}

/**
 * Cabecera tipo «seleccionar todo» (p. ej. «Todas las plazas»).
 * Usar `checked` + `indeterminate` como en {@link Checkbox}: si hay selección parcial, `checked: false` e `indeterminate: true`.
 */
export interface SearchMultiCheckboxHeaderRow extends BaseListItem {
  label: string;
  checked?: boolean;
  indeterminate?: boolean;
}

/**
 * Búsqueda en la variante `search-multi-checkbox` (API preferida: prop `search`).
 */
export interface ListBaseSearchMultiCheckboxSearchProps {
  /**
   * Si es `false`, no se muestra el buscador ni el filtrado por texto.
   * @default true
   */
  show?: boolean;
  /** Texto del campo de búsqueda (filtra por `label` de cada ítem). */
  placeholder?: string;
}

/** Cabecera e ítems en la variante `search-multi-checkbox` (API preferida: prop `list`). */
export interface ListBaseSearchMultiCheckboxListProps {
  /** Fila superior con checkbox (marcado / indeterminado / vacío). */
  header: SearchMultiCheckboxHeaderRow;
  items: SearchMultiCheckboxListItem[];
}

/** Callbacks en la variante `search-multi-checkbox` (API preferida: prop `events`). */
export interface ListBaseSearchMultiCheckboxEventsProps {
  onHeaderCheckboxChange?: (checked: boolean) => void;
  onItemCheckboxChange?: (itemId: string, checked: boolean) => void;
}

export interface AlphabeticGroup {
  letter: string;
  items: string[];
}

export interface SeparatorGroup {
  items: DefaultListItem[];
}

export interface UserProfileUser {
  name: string;
  email: string;
  avatar?: React.ReactNode | string;
  badge?: string;
}

export interface UserProfileMenuItem extends BaseListItem {
  label: string;
  icon?: VisualSlotType;
  color?: 'blue' | 'red';
  iconColor?: IconFontColor;
  type?: 'item' | 'toggle' | 'separator';
}

// ============================================================================
// LIST PROPS WITH DISCRIMINATED UNIONS
// ============================================================================
//
// API agrupada: por variante, datos y callbacks van en `list`, `selection`, `events`, `labels`, `profile`,
// `menu`, `options`, `content` y (solo search-multi-checkbox) `search`. Si existe la misma clave en bloque
// y en la raíz, gana el bloque. Tipos: `ListBase*VariantProps`, `InternalListProps`. Guía:
// packages/ui/DEVELOPMENT_GUIDE.md — «ListBase y Dropdown».

type ListBaseProps = {
  className?: string;
};

/** Props tipadas solo para {@link ListBase} con `variant="search-multi-checkbox"`. */
export type ListBaseSearchMultiCheckboxVariantProps = ListBaseProps & {
  variant: 'search-multi-checkbox';
  search?: Partial<ListBaseSearchMultiCheckboxSearchProps>;
  events?: Partial<ListBaseSearchMultiCheckboxEventsProps>;
  /** @deprecated Usar `search.show`. */
  showSearch?: boolean;
  /** @deprecated Usar `search.placeholder`. */
  searchPlaceholder?: string;
  /** @deprecated Usar `events.onHeaderCheckboxChange`. */
  onHeaderCheckboxChange?: (checked: boolean) => void;
  /** @deprecated Usar `events.onItemCheckboxChange`. */
  onItemCheckboxChange?: (itemId: string, checked: boolean) => void;
} & (
    | {
        list: ListBaseSearchMultiCheckboxListProps;
        /** @deprecated Usar `list.header`. */
        header?: SearchMultiCheckboxHeaderRow;
        /** @deprecated Usar `list.items`. */
        items?: SearchMultiCheckboxListItem[];
      }
    | {
        /** @deprecated Usar `list.header`. */
        header: SearchMultiCheckboxHeaderRow;
        /** @deprecated Usar `list.items`. */
        items: SearchMultiCheckboxListItem[];
        list?: undefined;
      }
  );

export type ListBaseDefaultVariantProps = ListBaseProps & {
  variant: 'default';
  list?: Partial<{ items: DefaultListItem[] }>;
  selection?: Partial<{ currentValue?: string }>;
  events?: Partial<{ onItemClick?: (item: DefaultListItem) => void }>;
  /** @deprecated Usar `list.items`. */
  items?: DefaultListItem[];
  /** @deprecated Usar `selection.currentValue`. */
  currentValue?: string;
  /** @deprecated Usar `events.onItemClick`. */
  onItemClick?: (item: DefaultListItem) => void;
} & ({ list: { items: DefaultListItem[] } } | { items: DefaultListItem[]; list?: undefined });

export type ListBaseSeparatorVariantProps = ListBaseProps & {
  variant: 'separator';
  list?: Partial<{ groups: SeparatorGroup[] }>;
  selection?: Partial<{ currentValue?: string }>;
  events?: Partial<{ onItemClick?: (itemId: string) => void }>;
  /** @deprecated Usar `list.groups`. */
  groups?: SeparatorGroup[];
  /** @deprecated Usar `selection.currentValue`. */
  currentValue?: string;
  /** @deprecated Usar `events.onItemClick`. */
  onItemClick?: (itemId: string) => void;
} & ({ list: { groups: SeparatorGroup[] } } | { groups: SeparatorGroup[]; list?: undefined });

export type ListBaseRadioVariantProps = ListBaseProps & {
  variant: 'radio';
  list?: Partial<{ items: RadioListItem[] }>;
  selection?: Partial<{ selectedValue?: string }>;
  events?: Partial<{ onSelectionChange?: (itemId: string) => void }>;
  /** @deprecated Usar `list.items`. */
  items?: RadioListItem[];
  /** @deprecated Usar `selection.selectedValue`. */
  selectedValue?: string;
  /** @deprecated Usar `events.onSelectionChange`. */
  onSelectionChange?: (itemId: string) => void;
} & ({ list: { items: RadioListItem[] } } | { items: RadioListItem[]; list?: undefined });

export type ListBaseUsersSelectVariantProps = ListBaseProps & {
  variant: 'users-select';
  list?: Partial<{ users: UserListItem[] }>;
  labels?: Partial<{ searchPlaceholder?: string; deleteButtonLabel?: string }>;
  events?: Partial<{
    onUserToggle?: (userId: string) => void;
    onDelete?: (userIds: string[]) => void;
  }>;
  /** @deprecated Usar `list.users`. */
  users?: UserListItem[];
  /** @deprecated Usar `labels.searchPlaceholder`. */
  searchPlaceholder?: string;
  /** @deprecated Usar `labels.deleteButtonLabel`. */
  deleteButtonLabel?: string;
  /** @deprecated Usar `events.onUserToggle`. */
  onUserToggle?: (userId: string) => void;
  /** @deprecated Usar `events.onDelete`. */
  onDelete?: (userIds: string[]) => void;
} & ({ list: { users: UserListItem[] } } | { users: UserListItem[]; list?: undefined });

export type ListBaseToggleVariantProps = ListBaseProps & {
  variant: 'toggle';
  list?: Partial<{ items: ToggleListItem[] }>;
  events?: Partial<{
    onToggleChange?: (itemId: string, checked: boolean) => void;
    onItemsReorder?: (reorderedItems: ToggleListItem[]) => void;
  }>;
  options?: Partial<{ enableReordering?: boolean }>;
  /** @deprecated Usar `list.items`. */
  items?: ToggleListItem[];
  /** @deprecated Usar `events.onToggleChange`. */
  onToggleChange?: (itemId: string, checked: boolean) => void;
  /** @deprecated Usar `events.onItemsReorder`. */
  onItemsReorder?: (reorderedItems: ToggleListItem[]) => void;
  /** @deprecated Usar `options.enableReordering`. */
  enableReordering?: boolean;
} & ({ list: { items: ToggleListItem[] } } | { items: ToggleListItem[]; list?: undefined });

export type ListBaseUserListVariantProps = ListBaseProps & {
  variant: 'user-list';
  list?: Partial<{ users: UserListItem[] }>;
  selection?: Partial<{ selectedUserId?: string }>;
  events?: Partial<{ onUserClick?: (userId: string) => void }>;
  /** @deprecated Usar `list.users`. */
  users?: UserListItem[];
  /** @deprecated Usar `selection.selectedUserId`. */
  selectedUserId?: string;
  /** @deprecated Usar `events.onUserClick`. */
  onUserClick?: (userId: string) => void;
} & ({ list: { users: UserListItem[] } } | { users: UserListItem[]; list?: undefined });

export type ListBaseCheckboxVariantProps = ListBaseProps & {
  variant: 'checkbox';
  list?: Partial<{ items: CheckboxListItem[] }>;
  events?: Partial<{ onCheckboxChange?: (itemId: string, checked: boolean) => void }>;
  /** @deprecated Usar `list.items`. */
  items?: CheckboxListItem[];
  /** @deprecated Usar `events.onCheckboxChange`. */
  onCheckboxChange?: (itemId: string, checked: boolean) => void;
} & ({ list: { items: CheckboxListItem[] } } | { items: CheckboxListItem[]; list?: undefined });

export type ListBaseUserProfileVariantProps = ListBaseProps & {
  variant: 'user-profile';
  profile?: Partial<{ user: UserProfileUser }>;
  menu?: Partial<{ items: UserProfileMenuItem[] }>;
  selection?: Partial<{ selectedItemId?: string; darkMode?: boolean }>;
  events?: Partial<{
    onMenuItemClick?: (itemId: string) => void;
    onDarkModeToggle?: (enabled: boolean) => void;
  }>;
  /** @deprecated Usar `profile.user`. */
  user?: UserProfileUser;
  /** @deprecated Usar `menu.items`. */
  menuItems?: UserProfileMenuItem[];
  /** @deprecated Usar `selection.selectedItemId`. */
  selectedItemId?: string;
  /** @deprecated Usar `selection.darkMode`. */
  darkMode?: boolean;
  /** @deprecated Usar `events.onMenuItemClick`. */
  onMenuItemClick?: (itemId: string) => void;
  /** @deprecated Usar `events.onDarkModeToggle`. */
  onDarkModeToggle?: (enabled: boolean) => void;
} & (
    | { profile: { user: UserProfileUser }; menu: { items: UserProfileMenuItem[] } }
    | {
        user: UserProfileUser;
        menuItems: UserProfileMenuItem[];
        profile?: undefined;
        menu?: undefined;
      }
  );

export type ListBaseAlphabeticVariantProps = ListBaseProps & {
  variant: 'alphabetic';
  list?: Partial<{ groups: AlphabeticGroup[] }>;
  selection?: Partial<{ selectedItem?: string }>;
  events?: Partial<{
    onItemClick?: (item: string, groupIndex: number, itemIndex: number) => void;
  }>;
  /** @deprecated Usar `list.groups`. */
  groups?: AlphabeticGroup[];
  /** @deprecated Usar `selection.selectedItem`. */
  selectedItem?: string;
  /** @deprecated Usar `events.onItemClick`. */
  onItemClick?: (item: string, groupIndex: number, itemIndex: number) => void;
} & ({ list: { groups: AlphabeticGroup[] } } | { groups: AlphabeticGroup[]; list?: undefined });

export type ListBaseFiltersVariantProps = ListBaseProps & {
  variant: 'filters';
  list?: Partial<{ items: FilterListItem[] }>;
  labels?: Partial<{ title?: string }>;
  selection?: Partial<{ selectedItemId?: string }>;
  events?: Partial<{ onItemClick?: (itemId: string) => void }>;
  /** @deprecated Usar `list.items`. */
  items?: FilterListItem[];
  /** @deprecated Usar `labels.title`. */
  title?: string;
  /** @deprecated Usar `selection.selectedItemId`. */
  selectedItemId?: string;
  /** @deprecated Usar `events.onItemClick`. */
  onItemClick?: (itemId: string) => void;
} & ({ list: { items: FilterListItem[] } } | { items: FilterListItem[]; list?: undefined });

export type ListBaseFiltersCheckboxVariantProps = ListBaseProps & {
  variant: 'filters-checkbox';
  list?: Partial<{ items: FilterListItem[] }>;
  labels?: Partial<{ title?: string; clearLabel?: string; applyLabel?: string }>;
  events?: Partial<{
    onItemToggle?: (itemId: string) => void;
    onClear?: () => void;
    onApply?: (selectedIds: string[]) => void;
    onBack?: () => void;
  }>;
  /** @deprecated Usar `list.items`. */
  items?: FilterListItem[];
  /** @deprecated Usar `labels.title`. */
  title?: string;
  /** @deprecated Usar `labels.clearLabel`. */
  clearLabel?: string;
  /** @deprecated Usar `labels.applyLabel`. */
  applyLabel?: string;
  /** @deprecated Usar `events.onItemToggle`. */
  onItemToggle?: (itemId: string) => void;
  /** @deprecated Usar `events.onClear`. */
  onClear?: () => void;
  /** @deprecated Usar `events.onApply`. */
  onApply?: (selectedIds: string[]) => void;
  /** @deprecated Usar `events.onBack`. */
  onBack?: () => void;
} & ({ list: { items: FilterListItem[] } } | { items: FilterListItem[]; list?: undefined });

export type ListBaseEmptyVariantProps = ListBaseProps & {
  variant: 'empty';
  labels?: Partial<{ title?: string; description?: string }>;
  content?: Partial<{ icon?: IconType }>;
  /** @deprecated Usar `labels.title`. */
  title?: string;
  /** @deprecated Usar `labels.description`. */
  description?: string;
  /** @deprecated Usar `content.icon`. */
  icon?: IconType;
};

/** Unión de todas las variantes de {@link ListBase}; cada rama coincide con un `ListBase*VariantProps` exportado. */
export type InternalListProps =
  | ListBaseDefaultVariantProps
  | ListBaseSeparatorVariantProps
  | ListBaseRadioVariantProps
  | ListBaseUsersSelectVariantProps
  | ListBaseToggleVariantProps
  | ListBaseUserListVariantProps
  | ListBaseCheckboxVariantProps
  | ListBaseUserProfileVariantProps
  | ListBaseAlphabeticVariantProps
  | ListBaseFiltersVariantProps
  | ListBaseFiltersCheckboxVariantProps
  | ListBaseEmptyVariantProps
  | ListBaseSearchMultiCheckboxVariantProps;

function resolveSearchMultiCheckboxVariant(props: ListBaseSearchMultiCheckboxVariantProps): {
  showSearch: boolean;
  searchPlaceholder: string;
  header: SearchMultiCheckboxHeaderRow;
  items: SearchMultiCheckboxListItem[];
  onHeaderCheckboxChange?: (checked: boolean) => void;
  onItemCheckboxChange?: (itemId: string, checked: boolean) => void;
} {
  const header = (props.list?.header ?? props.header) as SearchMultiCheckboxHeaderRow;
  const items = (props.list?.items ?? props.items) as SearchMultiCheckboxListItem[];
  const search = props.search;
  return {
    showSearch: search?.show ?? props.showSearch ?? true,
    searchPlaceholder: search?.placeholder ?? props.searchPlaceholder ?? 'Buscar...',
    header,
    items,
    onHeaderCheckboxChange: props.events?.onHeaderCheckboxChange ?? props.onHeaderCheckboxChange,
    onItemCheckboxChange: props.events?.onItemCheckboxChange ?? props.onItemCheckboxChange,
  };
}

function resolveDefaultVariant(props: ListBaseDefaultVariantProps) {
  return {
    items: (props.list?.items ?? props.items) as DefaultListItem[],
    currentValue: props.selection?.currentValue ?? props.currentValue,
    onItemClick: props.events?.onItemClick ?? props.onItemClick,
  };
}

function resolveSeparatorVariant(props: ListBaseSeparatorVariantProps) {
  return {
    groups: (props.list?.groups ?? props.groups) as SeparatorGroup[],
    currentValue: props.selection?.currentValue ?? props.currentValue,
    onItemClick: props.events?.onItemClick ?? props.onItemClick,
  };
}

function resolveRadioVariant(props: ListBaseRadioVariantProps) {
  return {
    items: (props.list?.items ?? props.items) as RadioListItem[],
    selectedValue: props.selection?.selectedValue ?? props.selectedValue,
    onSelectionChange: props.events?.onSelectionChange ?? props.onSelectionChange,
  };
}

function resolveUsersSelectVariant(props: ListBaseUsersSelectVariantProps) {
  return {
    users: (props.list?.users ?? props.users) as UserListItem[],
    searchPlaceholder: props.labels?.searchPlaceholder ?? props.searchPlaceholder,
    deleteButtonLabel: props.labels?.deleteButtonLabel ?? props.deleteButtonLabel,
    onUserToggle: props.events?.onUserToggle ?? props.onUserToggle,
    onDelete: props.events?.onDelete ?? props.onDelete,
  };
}

function resolveToggleVariant(props: ListBaseToggleVariantProps) {
  return {
    items: (props.list?.items ?? props.items) as ToggleListItem[],
    onToggleChange: props.events?.onToggleChange ?? props.onToggleChange,
    onItemsReorder: props.events?.onItemsReorder ?? props.onItemsReorder,
    enableReordering: props.options?.enableReordering ?? props.enableReordering,
  };
}

function resolveUserListVariant(props: ListBaseUserListVariantProps) {
  return {
    users: (props.list?.users ?? props.users) as UserListItem[],
    selectedUserId: props.selection?.selectedUserId ?? props.selectedUserId,
    onUserClick: props.events?.onUserClick ?? props.onUserClick,
  };
}

function resolveCheckboxVariant(props: ListBaseCheckboxVariantProps) {
  return {
    items: (props.list?.items ?? props.items) as CheckboxListItem[],
    onCheckboxChange: props.events?.onCheckboxChange ?? props.onCheckboxChange,
  };
}

function resolveUserProfileVariant(props: ListBaseUserProfileVariantProps) {
  const user = (props.profile?.user ?? props.user) as UserProfileUser;
  const menuItems = (props.menu?.items ?? props.menuItems) as UserProfileMenuItem[];
  return {
    user,
    menuItems,
    selectedItemId: props.selection?.selectedItemId ?? props.selectedItemId,
    darkMode: props.selection?.darkMode ?? props.darkMode,
    onMenuItemClick: props.events?.onMenuItemClick ?? props.onMenuItemClick,
    onDarkModeToggle: props.events?.onDarkModeToggle ?? props.onDarkModeToggle,
  };
}

function resolveAlphabeticVariant(props: ListBaseAlphabeticVariantProps) {
  return {
    groups: (props.list?.groups ?? props.groups) as AlphabeticGroup[],
    selectedItem: props.selection?.selectedItem ?? props.selectedItem,
    onItemClick: props.events?.onItemClick ?? props.onItemClick,
  };
}

function resolveFiltersVariant(props: ListBaseFiltersVariantProps) {
  return {
    title: props.labels?.title ?? props.title,
    items: (props.list?.items ?? props.items) as FilterListItem[],
    selectedItemId: props.selection?.selectedItemId ?? props.selectedItemId,
    onItemClick: props.events?.onItemClick ?? props.onItemClick,
  };
}

function resolveFiltersCheckboxVariant(props: ListBaseFiltersCheckboxVariantProps) {
  return {
    title: props.labels?.title ?? props.title,
    items: (props.list?.items ?? props.items) as FilterListItem[],
    clearLabel: props.labels?.clearLabel ?? props.clearLabel,
    applyLabel: props.labels?.applyLabel ?? props.applyLabel,
    onItemToggle: props.events?.onItemToggle ?? props.onItemToggle,
    onClear: props.events?.onClear ?? props.onClear,
    onApply: props.events?.onApply ?? props.onApply,
    onBack: props.events?.onBack ?? props.onBack,
  };
}

function resolveEmptyVariant(props: ListBaseEmptyVariantProps) {
  return {
    title: props.labels?.title ?? props.title,
    description: props.labels?.description ?? props.description,
    icon: props.content?.icon ?? props.icon,
  };
}

// ============================================================================
// MAIN LIST COMPONENT
// ============================================================================

/**
 * Lista base con múltiples variantes (menú, filtros, multiselección, etc.).
 *
 * **API agrupada (recomendada):** según variante, usar bloques `list` (ítems, grupos, usuarios, etc.),
 * `selection` (valor seleccionado / id), `events` (callbacks), `labels` (títulos, placeholders),
 * `profile` + `menu` (`user-profile`), `options` (p. ej. `enableReordering` en `toggle`), `content` (`empty`),
 * y en `search-multi-checkbox` además `search`. Tabla por variante y `Dropdown`: `packages/ui/DEVELOPMENT_GUIDE.md`.
 *
 * @example
 * ```tsx
 * <ListBase
 *   variant="default"
 *   list={{ items: [{ id: '1', label: 'Cuenta' }] }}
 *   selection={{ currentValue: '1' }}
 *   events={{ onItemClick: (item) => {} }}
 * />
 *
 * <ListBase
 *   variant="search-multi-checkbox"
 *   search={{ placeholder: 'Buscar…' }}
 *   list={{
 *     header: { id: 'all', label: 'Todas las plazas', checked: false, indeterminate: true },
 *     items: [
 *       { id: '1', label: 'Acapulco', checked: true },
 *       { id: '2', label: 'Actopan', checked: true },
 *     ],
 *   }}
 *   events={{
 *     onHeaderCheckboxChange: (v) => {},
 *     onItemCheckboxChange: (id, v) => {},
 *   }}
 * />
 * ```
 */
export const ListBase: React.FC<InternalListProps> = (props) => {
  const { variant, className } = props;

  const baseContainerClass = cn(
    'ui-imj ui-max-h-[300px] custom-scrollbar ui-overflow-y-auto',
    className,
  );

  // ============================================================================
  // HOOKS - Todos los hooks deben estar al principio, antes de cualquier return condicional
  // ============================================================================
  // Hook para users-select variant
  const [searchTerm, setSearchTerm] = useState('');

  const toggleResolved = useMemo(
    () => (variant === 'toggle' ? resolveToggleVariant(props as ListBaseToggleVariantProps) : null),
    [variant, props],
  );
  const toggleItems = toggleResolved?.items ?? null;
  const [localItems, setLocalItems] = useState<ToggleListItem[]>(toggleItems ?? []);

  React.useEffect(() => {
    if (variant === 'toggle' && toggleItems !== null && toggleItems !== undefined) {
      setLocalItems(toggleItems);
    }
  }, [variant, toggleItems]);

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (variant !== 'toggle' || !toggleResolved) return;
      setLocalItems((currentItems: ToggleListItem[]) => {
        const reorderedItems = reorder(currentItems, fromIndex, toIndex);
        requestAnimationFrame(() => {
          toggleResolved.onItemsReorder?.(reorderedItems);
        });
        return reorderedItems;
      });
    },
    [variant, toggleResolved],
  );

  // Hook de drag and drop (solo para toggle variant)
  const dragAndDropResult = useDragAndDrop({
    onReorder: handleReorder,
    liveReorder: true,
    throttleMs: 50,
  });

  const { handlers, draggedItemId, dragOverItemId } =
    variant === 'toggle'
      ? dragAndDropResult
      : { handlers: null, draggedItemId: null, dragOverItemId: null };

  const listBaseDeprecationSigRef = useRef('');
  warnDeprecatedListBaseFlatPropsIfNeeded(props, listBaseDeprecationSigRef);

  // ============================================================================
  // VARIANT: DEFAULT
  // ============================================================================
  if (variant === 'default') {
    const { items, currentValue, onItemClick } = resolveDefaultVariant(
      props as ListBaseDefaultVariantProps,
    );
    return (
      <div className={cn(baseContainerClass, 'ui-min-w-[208px]')}>
        <ul className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-8 ui-p-0">
          {items.map((item) => (
            <ListItemDefault
              key={item.id}
              icon={item.icon}
              iconColor={item.iconColor}
              disabled={item.disabled}
              current={currentValue === item.id}
              color={item.color}
              onClick={() => onItemClick?.(item)}
            >
              {item.label}
            </ListItemDefault>
          ))}
        </ul>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: SEPARATOR
  // ============================================================================
  if (variant === 'separator') {
    const { groups, currentValue, onItemClick } = resolveSeparatorVariant(
      props as ListBaseSeparatorVariantProps,
    );
    return (
      <div className={cn(baseContainerClass, 'ui-min-w-[208px]')}>
        <ul className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-8 ui-p-0">
          {groups.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && <ListSeparator />}
              {group.items.map((item) => (
                <ListItemDefault
                  key={item.id}
                  icon={item.icon}
                  disabled={item.disabled}
                  current={currentValue === item.id}
                  onClick={() => onItemClick?.(item.id)}
                >
                  {item.label}
                </ListItemDefault>
              ))}
            </React.Fragment>
          ))}
        </ul>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: RADIO
  // ============================================================================
  if (variant === 'radio') {
    const { items, selectedValue, onSelectionChange } = resolveRadioVariant(
      props as ListBaseRadioVariantProps,
    );
    return (
      <div className={cn(baseContainerClass, 'ui-min-w-[195px]')}>
        <div className="ui-flex ui-flex-col ui-gap-8">
          {items.map((item) => (
            <ListItemRadio
              key={item.id}
              selected={selectedValue === item.id}
              disabled={item.disabled}
              onClick={() => !item.disabled && onSelectionChange?.(item.id)}
              rightContent={item.count ? `(${item.count})` : undefined}
            >
              {item.label}
            </ListItemRadio>
          ))}
        </div>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: USERS SELECT
  // ============================================================================
  if (variant === 'users-select') {
    const {
      users,
      searchPlaceholder = 'Buscar...',
      deleteButtonLabel = '🗑️ Borrar Usuario',
      onUserToggle,
      onDelete,
    } = resolveUsersSelectVariant(props as ListBaseUsersSelectVariantProps);

    const selectedUsers = users.filter((user) => user.selected);
    const filteredUsers = users.filter((user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return (
      <div className={cn(baseContainerClass, 'ui-flex ui-min-w-[240px] ui-flex-col ui-gap-8')}>
        <ListSearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={searchPlaceholder}
        />

        {filteredUsers.map((user) => (
          <div
            key={user.id}
            onClick={() => onUserToggle?.(user.id)}
            className={cn(
              'ui-flex ui-cursor-pointer ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6 ui-transition-all',
              {
                'hover:ui-bg-fill-hover': true,
              },
            )}
          >
            <div className="ui-flex ui-h-16 ui-w-16 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-surface-secondary ui-text-body-xs-regular">
              {user.avatar || user.name.substring(0, 1).toUpperCase()}
            </div>
            <span className="ui-flex-1 ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
              {user.name}
            </span>
            <Checkbox
              checked={user.selected || false}
              onChange={() => onUserToggle?.(user.id)}
              size="md"
            />
          </div>
        ))}

        {selectedUsers.length > 0 && (
          <button
            onClick={() => {
              const selectedIds = selectedUsers.map((user) => user.id);
              onDelete?.(selectedIds);
            }}
            className="ui-h-32 ui-w-full ui-rounded-control-sm ui-bg-surface-danger ui-px-8 ui-py-4 ui-text-body-sm-regular ui-font-body-sm-bold ui-text-danger ui-transition-colors hover:ui-bg-surface-danger-hover"
          >
            {deleteButtonLabel}
          </button>
        )}
      </div>
    );
  }

  // ============================================================================
  // VARIANT: TOGGLE
  // ============================================================================
  if (variant === 'toggle') {
    if (!toggleResolved) {
      return null;
    }
    const { onToggleChange, enableReordering = false } = toggleResolved;

    if (!handlers) {
      return null;
    }

    return (
      <div
        className={cn(
          baseContainerClass,
          'ui-flex ui-min-w-[320px] ui-flex-col ui-gap-8',
          '[&>*]:ui-transition-transform [&>*]:ui-duration-200 [&>*]:ui-ease-out',
        )}
      >
        {localItems.map((item: ToggleListItem, index: number) => {
          const isDragging = enableReordering && draggedItemId === item.id;
          const isDragOver = enableReordering && dragOverItemId === item.id;
          // Crear el icono con color según el estado disabled del item
          const dragIcon = enableReordering ? (
            <Icon
              name={faGripDotsVertical}
              size="sm"
              color={item.disabled ? 'disabled' : 'secondary'}
            />
          ) : undefined;

          return (
            <ListItemToggle
              key={item.id}
              title={item.title}
              description={item.description}
              checked={item.checked}
              disabled={item.disabled}
              onChange={(checked) => onToggleChange?.(item.id, checked)}
              draggable={enableReordering && (item.draggable ?? true)}
              isDragging={isDragging}
              isDragOver={isDragOver}
              onDragStart={(e) => handlers.onDragStart(e, item.id, index)}
              onDragEnd={handlers.onDragEnd}
              onDragOver={(e) => handlers.onDragOver(e, item.id, index)}
              onDragLeave={handlers.onDragLeave}
              onDrop={(e) => handlers.onDrop(e, item.id, index)}
              dragHandleIcon={dragIcon}
            />
          );
        })}
      </div>
    );
  }

  // ============================================================================
  // VARIANT: USER LIST
  // ============================================================================
  if (variant === 'user-list') {
    const { users, selectedUserId, onUserClick } = resolveUserListVariant(
      props as ListBaseUserListVariantProps,
    );
    return (
      <div className={cn(baseContainerClass, 'ui-flex ui-min-w-[320px] ui-flex-col ui-gap-8')}>
        {users.map((user) => (
          <ListItemUser
            key={user.id}
            name={user.name}
            subtitle={user.subtitle}
            avatar={user.avatar}
            badge={user.badge}
            badgeVariant={user.badgeVariant}
            selected={selectedUserId === user.id}
            onClick={() => onUserClick?.(user.id)}
          />
        ))}
      </div>
    );
  }

  // ============================================================================
  // VARIANT: CHECKBOX
  // ============================================================================
  if (variant === 'checkbox') {
    const { items, onCheckboxChange } = resolveCheckboxVariant(
      props as ListBaseCheckboxVariantProps,
    );
    return (
      <div className={cn(baseContainerClass, 'ui-flex ui-min-w-[320px] ui-flex-col ui-gap-8')}>
        {items.map((item) => (
          <ListItemCheckbox
            key={item.id}
            title={item.title}
            description={item.description}
            checked={item.checked}
            disabled={item.disabled}
            onChange={(checked) => onCheckboxChange?.(item.id, checked)}
          />
        ))}
      </div>
    );
  }

  // ============================================================================
  // VARIANT: USER PROFILE
  // ============================================================================
  if (variant === 'user-profile') {
    const {
      user,
      menuItems,
      selectedItemId,
      darkMode = false,
      onMenuItemClick,
      onDarkModeToggle,
    } = resolveUserProfileVariant(props as ListBaseUserProfileVariantProps);

    const renderAvatar = () => {
      if (typeof user.avatar === 'string') {
        return (
          <div className="ui-flex ui-h-32 ui-w-32 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-fill-brand ui-text-body-sm-regular ui-font-body-sm-semibold ui-text-on-color">
            {user.avatar}
          </div>
        );
      }
      return (
        user.avatar || (
          <div className="ui-flex ui-h-32 ui-w-32 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-fill-brand ui-text-body-sm-regular ui-font-body-sm-semibold ui-text-on-color">
            {user.name.substring(0, 2).toUpperCase()}
          </div>
        )
      );
    };

    return (
      <div
        className={cn(
          baseContainerClass,
          'ui-flex ui-w-[298px] ui-flex-col ui-gap-8 ui-overflow-clip',
        )}
      >
        {/* User Header */}
        <div className="ui-flex ui-h-56 ui-items-center ui-gap-8 ui-rounded-control-sm ui-bg-fill-hover ui-px-8 ui-py-6">
          {renderAvatar()}
          <div className="ui-min-w-0 ui-flex-1">
            <div className="ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
              {user.name}
            </div>
            <div className="ui-truncate ui-text-body-xs-regular ui-text-tertiary">{user.email}</div>
          </div>
          {user.badge && (
            <span className="ui-shrink-0 ui-rounded-control-sm ui-bg-fill-brand ui-px-8 ui-py-2 ui-text-body-xs-regular ui-font-body-xs-semibold ui-text-on-color">
              {user.badge}
            </span>
          )}
        </div>

        {/* Menu Items */}
        <ul className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-8 ui-p-0">
          {menuItems.map((item, index) => {
            if (item.type === 'separator') {
              return <ListSeparator key={`separator-${index}`} />;
            }

            if (item.type === 'toggle') {
              return (
                <li
                  key={item.id}
                  className="ui-flex ui-h-32 ui-items-center ui-justify-between ui-rounded-control-sm ui-px-8 ui-py-6"
                >
                  <div className="ui-flex ui-items-center ui-gap-8">
                    {item.icon && (
                      <span className="ui-flex ui-items-center ui-justify-center ui-text-tertiary">
                        <ListItemDefault icon={item.icon} onClick={() => {}}>
                          {item.label}
                        </ListItemDefault>
                      </span>
                    )}
                  </div>
                  <div
                    onClick={() => onDarkModeToggle?.(!darkMode)}
                    className={cn(
                      'ui-flex ui-h-20 ui-w-32 ui-cursor-pointer ui-items-center ui-rounded-2xl ui-px-2 ui-transition-all',
                      {
                        'ui-justify-end ui-bg-fill-brand': darkMode,
                        'ui-justify-start ui-bg-fill-brand-disabled': !darkMode,
                      },
                    )}
                  >
                    <div className="ui-h-16 ui-w-16 ui-rounded-control-pill ui-bg-fill ui-shadow-lg" />
                  </div>
                </li>
              );
            }

            return (
              <ListItemDefault
                key={item.id}
                icon={item.icon}
                iconColor={item.iconColor}
                current={selectedItemId === item.id}
                color={item.color}
                onClick={() => onMenuItemClick?.(item.id)}
              >
                {item.label}
              </ListItemDefault>
            );
          })}
        </ul>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: ALPHABETIC
  // ============================================================================
  if (variant === 'alphabetic') {
    const { groups, selectedItem, onItemClick } = resolveAlphabeticVariant(
      props as ListBaseAlphabeticVariantProps,
    );
    return (
      <div className={cn(baseContainerClass, 'ui-flex ui-min-w-[208px] ui-flex-col ui-gap-8')}>
        {groups.map((group, groupIndex) => (
          <div key={group.letter}>
            <div className="ui-mb-8 ui-text-body-xs-regular ui-font-body-xs-semibold ui-text-secondary">
              {group.letter}
            </div>
            <div className="ui-flex ui-flex-col ui-gap-8">
              {group.items.map((item, itemIndex) => {
                const isSelected = selectedItem === item;
                return (
                  <div
                    key={`${group.letter}-${itemIndex}`}
                    onClick={() => onItemClick?.(item, groupIndex, itemIndex)}
                    className={cn(
                      'ui-cursor-pointer ui-rounded-control-sm ui-px-8 ui-py-6 ui-text-body-sm-regular ui-font-body-sm-medium ui-transition-all',
                      {
                        // Selected: azul oscuro
                        'ui-bg-fill-brand ui-text-on-color': isSelected,
                        // Default text
                        'ui-text-primary': !isSelected,
                        // Hover: gris claro (solo cuando no está seleccionado)
                        'hover:ui-bg-fill-hover': !isSelected,
                      },
                    )}
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ============================================================================
  // VARIANT: FILTERS
  // ============================================================================
  if (variant === 'filters') {
    const {
      title = 'Filtrar Por',
      items,
      selectedItemId,
      onItemClick,
    } = resolveFiltersVariant(props as ListBaseFiltersVariantProps);
    return (
      <div className={cn(baseContainerClass, 'ui-flex ui-min-w-[208px] ui-flex-col ui-gap-8')}>
        <div className="ui-text-body-sm-regular ui-font-body-sm-bold ui-text-primary">{title}</div>
        <ListSeparator />
        <div className="ui-flex ui-flex-col ui-gap-8">
          {items.map((item) => {
            const isSelected = selectedItemId === item.id;
            return (
              <div
                key={item.id}
                onClick={() => onItemClick?.(item.id)}
                className={cn(
                  'ui-cursor-pointer ui-rounded-control-sm ui-px-8 ui-py-6 ui-text-body-sm-regular ui-font-body-sm-medium ui-transition-all',
                  {
                    // Selected: azul oscuro
                    'ui-bg-fill-brand ui-text-on-color': isSelected,
                    // Default text
                    'ui-text-primary': !isSelected,
                    // Hover: gris claro (solo cuando no está seleccionado)
                    'hover:ui-bg-fill-hover': !isSelected,
                  },
                )}
              >
                {item.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: FILTERS CHECKBOX
  // ============================================================================
  if (variant === 'filters-checkbox') {
    const {
      title = 'Headline',
      items,
      clearLabel = '🧹 Limpiar',
      applyLabel = 'Ok',
      onItemToggle,
      onClear,
      onApply,
      onBack,
    } = resolveFiltersCheckboxVariant(props as ListBaseFiltersCheckboxVariantProps);

    const selectedIds = items.filter((item) => item.checked).map((item) => item.id);

    return (
      <div className={cn(baseContainerClass, 'ui-flex ui-min-w-[208px] ui-flex-col ui-gap-8')}>
        <ListHeader title={title} onBackClick={onBack} />
        <ListSeparator />
        <div className="ui-flex ui-flex-col ui-gap-8">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => onItemToggle?.(item.id)}
              className={cn(
                'ui-flex ui-cursor-pointer ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6 ui-transition-all',
                {
                  'hover:ui-bg-fill-hover': true,
                },
              )}
            >
              <Checkbox
                checked={item.checked || false}
                onChange={() => onItemToggle?.(item.id)}
                size="md"
              />
              <span className="ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
                {item.label}
              </span>
            </div>
          ))}
        </div>
        <ListSeparator />
        <div className="ui-flex ui-gap-8">
          <button
            onClick={onClear}
            className="ui-h-20 ui-flex-1 ui-rounded-control-sm ui-border ui-border-default ui-bg-surface-secondary ui-px-8 ui-py-4 ui-text-body-xs-regular ui-font-body-xs-bold ui-text-primary ui-transition-colors hover:ui-bg-surface-secondary-hover"
          >
            {clearLabel}
          </button>
          <button
            onClick={() => onApply?.(selectedIds)}
            className="ui-h-20 ui-flex-1 ui-rounded-control-sm ui-bg-fill-brand ui-px-8 ui-py-4 ui-text-body-xs-regular ui-font-body-xs-bold ui-text-on-color ui-transition-colors hover:ui-bg-fill-brand-hover"
          >
            {applyLabel}
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: SEARCH + MULTI CHECKBOX (cabecera + ítems, diseño tipo OBP / plazas)
  // ============================================================================
  if (variant === 'search-multi-checkbox') {
    const {
      showSearch,
      searchPlaceholder,
      header,
      items,
      onHeaderCheckboxChange,
      onItemCheckboxChange,
    } = resolveSearchMultiCheckboxVariant(props as ListBaseSearchMultiCheckboxVariantProps);

    const filteredItems = showSearch
      ? items.filter((item) => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
      : items;

    const headerChecked = header.checked ?? false;
    const headerIndeterminate = Boolean(header.indeterminate) && !headerChecked;
    const headerInputId = `list-search-multi-header-${header.id}`;

    return (
      <div
        className={cn(
          baseContainerClass,
          'ui-flex ui-min-w-[240px] ui-flex-col ui-gap-8 ui-bg-fill ui-p-8',
          className,
        )}
      >
        {showSearch ? (
          <ListSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={searchPlaceholder}
          />
        ) : null}

        <div
          className={cn(
            'ui-flex ui-min-h-32 ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6 ui-transition-all',
            header.disabled ? 'ui-opacity-50' : 'hover:ui-bg-fill-hover',
          )}
        >
          {header.disabled ? (
            <span className="ui-min-w-0 ui-flex-1 ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
              {header.label}
            </span>
          ) : (
            <label
              htmlFor={headerInputId}
              className="ui-m-0 ui-min-w-0 ui-flex-1 ui-cursor-pointer ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary"
            >
              {header.label}
            </label>
          )}
          <Checkbox
            id={headerInputId}
            checked={headerChecked}
            indeterminate={headerIndeterminate}
            onChange={(next) => onHeaderCheckboxChange?.(next)}
            disabled={header.disabled}
            size="md"
          />
        </div>

        <ListSeparator />

        <div className="ui-flex ui-flex-col ui-gap-8">
          {filteredItems.map((item) => {
            const itemInputId = `list-search-multi-item-${item.id}`;
            return (
              <div
                key={item.id}
                className={cn(
                  'ui-flex ui-min-h-32 ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6 ui-transition-all',
                  item.disabled ? 'ui-cursor-not-allowed ui-opacity-50' : 'hover:ui-bg-fill-hover',
                )}
              >
                {item.disabled ? (
                  <span className="ui-min-w-0 ui-flex-1 ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
                    {item.label}
                  </span>
                ) : (
                  <label
                    htmlFor={itemInputId}
                    className="ui-m-0 ui-min-w-0 ui-flex-1 ui-cursor-pointer ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary"
                  >
                    {item.label}
                  </label>
                )}
                <Checkbox
                  id={itemInputId}
                  checked={item.checked || false}
                  onChange={(next) => onItemCheckboxChange?.(item.id, next)}
                  disabled={item.disabled}
                  size="md"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ============================================================================
  // VARIANT: EMPTY
  // ============================================================================
  if (variant === 'empty') {
    const { title, description, icon } = resolveEmptyVariant(props as ListBaseEmptyVariantProps);
    return (
      <div className={cn(baseContainerClass, 'ui-min-w-[313px]')}>
        <ListEmpty title={title} description={description} icon={icon as IconType} />
      </div>
    );
  }

  return null;
};
